"use client";

import React, { useMemo } from 'react';
import clsx from 'clsx';
import { isFilled } from '@prismicio/helpers';
import { Settings } from '@/app/lib/settings';
import { ConditionalLink } from '@/app/lib/ConditionalLink';
import { PrismicNextImage } from '@prismicio/next';
import { MoreLink } from '@/app/lib/MoreLink';
import { RevealOnScroll } from '@/app/lib/RevealOnScroll';
import Chevron from '@/app/assets/images/icon_chevron.svg';
import styles from './projectsGrid.module.scss';
import sharedStyles from '@/app/shared.module.scss';
import { useRouter, usePathname } from 'next/navigation';

export function ProjectsGrid({ slice, projects }) {
	const router = useRouter();
	const pathname = usePathname();

	// Define the actual base path for the collection page
	const collectionBasePath = '/our-work';

	// Helper to convert filter key (e.g., 'Branding') to URL slug (e.g., 'branding')
	const keyToSlug = (key) => key.toLowerCase().replace(/ /g, '-');

	// Helper to convert URL slug (e.g., 'branding') back to Filter Key (e.g., 'Branding')
	// This Filter Key is what we use internally for Prismic data matching.
	const slugToKey = (slug) => {
		for (const key of Object.keys(Settings.projectTypes)) {
			if (keyToSlug(key) === slug) {
				return key; // e.g., 'Branding'
			}
		}
		return '';
	};

	// Determine the current filter state from the URL pathname
	const selectedFilter = useMemo(() => {
		const segments = pathname.split('/').filter(Boolean);

		// Find the index of the base path segment (e.g., 'our-work')
		const basePathIndex = segments.indexOf(collectionBasePath.substring(1));

		// If 'our-work' is found and there is a segment immediately after it, treat that as the filter slug
		if (basePathIndex !== -1 && segments.length > basePathIndex + 1) {
			return slugToKey(segments[basePathIndex + 1]); // e.g., 'Branding'
		}
		return ''; // URL is /our-work or /our-work/project-uid
	}, [pathname]);

	// --- FILTER TOGGLE (UPDATED FOR URL NAVIGATION & SHALLOW ROUTING) ---
	const toggleFilter = (filterKey) => {
		const slug = keyToSlug(filterKey); // 'Branding' -> 'branding'

		// Check if the current filter is the one being clicked (to toggle off)
		// Note: 'All' button has filterKey='All', which also toggles the filter off.
		const isTogglingOff = filterKey === 'All' || selectedFilter === filterKey;

		let newPath = collectionBasePath;

		if (!isTogglingOff) {
			// Selecting a new filter: Navigate to the base path + slug (e.g., /our-work/branding)
			newPath = `${collectionBasePath}/${slug}`;
		}

		// Use router.replace with shallow: true to update the URL without a full navigation/reload.
		// This will prevent the FOUC/page load issue.
		router.replace(newPath, { shallow: true });
	};

	const filteredProjects = useMemo(() => {
		if (selectedFilter.length === 0 || selectedFilter === 'All') {
			return projects;
		}

		return projects?.filter((project) => {
			// The project's type field is a multi-select array of objects, 
			// where each object has a 'type' property that holds the human-readable filter name (e.g., 'Branding')
			const projectTypesAsStrings = project?.data?.type?.map(item => item.type) || [];
			return projectTypesAsStrings.includes(selectedFilter);
		});
	}, [projects, selectedFilter]);

	// --- PROJECT CARD COMPONENT ---
	const ProjectCard = ({ project, index }) => {
		return (
			<ConditionalLink
				condition={project?.data?.slices?.length}
				url={`/${Settings.projectsPrefix}/${project?.uid}`}
				className={clsx(styles.project)}
				key={index}
			>
				{isFilled.image(project?.data?.image) && (
					<div className={styles.image_wrapper}>
						<PrismicNextImage field={project?.data?.image} fallbackAlt='' className={clsx(styles.image)} />
					</div>
				)}
				{isFilled.keyText(project?.data?.title) && (
					<h3 className={clsx(styles.project_title)}>{project?.data?.title}</h3>
				)}
				{isFilled.keyText(project?.data?.tagline) && (
					<p className={clsx(styles.tagline)}>{project?.data?.tagline}</p>
				)}
			</ConditionalLink>
		)
	}

	return (
		<div className={clsx(styles.wrapper)}>

			{/* filters */}
			{slice?.primary?.show_filter && (
				<div className={clsx(sharedStyles.filters)}>
					<div className={clsx(sharedStyles.label_wrap)}>
						<div className={clsx(sharedStyles.label)}>
							Filter
						</div>
						<Chevron className={sharedStyles.chevron} />
					</div>

					{Object.entries(Settings.projectTypes).map(([key, value]) => {
						// key is the filter value (e.g., 'Branding')
						const isHighlighted = selectedFilter === key;
						return (
							<span
								key={key}
								onClick={() => toggleFilter(key)}
								className={clsx(sharedStyles.item, { [sharedStyles.highlighted]: isHighlighted })}
							>
								{value}
							</span>
						)
					})}
				</div>
			)}

			{/* grid */}
			{filteredProjects?.length ? (
				<div className={clsx(styles.main)}>
					{filteredProjects.map((project, index) => (
						<RevealOnScroll scrollDelayRem={(index % 3) * 5} key={index}>
							<ProjectCard project={project} index={index} />
						</RevealOnScroll>
					))}
				</div>
			) : (
				<div className={styles.noResults}>No projects found for the selected filters.</div>
			)}

			{/* back link */}
			{isFilled.link(slice?.primary?.back_link) && (
				<MoreLink
					link={`/${slice?.primary?.back_link?.uid || slice?.primary?.back_link?.url}`}
					text={slice?.primary?.back_link?.text || "Back to studio sad"}
					back={true}
					slice={slice}
				/>
			)}

		</div>
	);
}